/**
 * Locked template artboard — drag posisi, resize per layer (Canva-style handles + pinch/wheel).
 * html2canvas: konversi cqw → px dari lebar artboard.
 */

const SCALE_MIN = 0.35;
const SCALE_MAX = 2.5;

export function normalizeCqwFonts(artboard, globalScale = 1, layerScales = {}) {
    if (!artboard) return;

    const width = artboard.getBoundingClientRect().width;
    if (!width) return;

    artboard.querySelectorAll('.locked-layer').forEach((layer) => {
        const cqw = parseFloat(layer.dataset.sizeCqw || '');
        if (!Number.isFinite(cqw)) return;

        const layerId = layer.dataset.layer || '';
        const scale = Number(layerScales[layerId] ?? layer.dataset.layerScale ?? globalScale) || 1;
        const px = (width * cqw * scale) / 100;
        const size = `${px}px`;
        layer.style.fontSize = size;

        const text = layer.querySelector('.locked-layer__text');
        if (text) text.style.fontSize = size;
    });
}

function clampScale(value) {
    return Math.min(SCALE_MAX, Math.max(SCALE_MIN, value));
}

function syncLayerText(artboard, bind, value) {
    artboard.querySelectorAll(`[data-bind="${bind}"] .locked-layer__text`).forEach((node) => {
        node.textContent = value.trim() || '\u00A0';
    });
}

function clientPoint(e) {
    return e.touches?.length
        ? { x: e.touches[0].clientX, y: e.touches[0].clientY }
        : { x: e.clientX, y: e.clientY };
}

function touchDistance(touches) {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.hypot(dx, dy);
}

function makeInteractiveLayer(layer, artboard, state) {
    const layerId = layer.dataset.layer;

    function currentScale() {
        return state.scales[layerId] ?? 1;
    }

    function setScale(next, { silent = false } = {}) {
        const scale = clampScale(next);
        state.scales[layerId] = scale;
        layer.dataset.layerScale = String(scale);
        normalizeCqwFonts(artboard, 1, state.scales);

        const hidden = document.getElementById(`scale-${layerId}`);
        if (hidden) hidden.value = scale.toFixed(2);

        if (!silent) layer.dispatchEvent(new CustomEvent('layer-scale', { detail: { scale } }));
    }

    function applyPosition(nx, ny) {
        layer.style.left = `${nx}%`;
        layer.style.top = `${ny}%`;
        const hx = document.getElementById(`pos-${layerId}-x`);
        const hy = document.getElementById(`pos-${layerId}-y`);
        if (hx) hx.value = nx.toFixed(1);
        if (hy) hy.value = ny.toFixed(1);
    }

    function selectLayer() {
        if (state.selected === layer) return;
        state.selected?.classList.remove('is-selected');
        state.selected = layer;
        layer.classList.add('is-selected');
    }

    // ── Drag posisi (geser layer) ─────────────────────────────
    let dragging = false;
    let touchPending = false;
    let sx;
    let sy;
    let sl;
    let st;

    function dragStart(e) {
        if (e.target.closest('.locked-layer__handle')) return;
        if (e.touches?.length > 1) return;

        selectLayer();

        const cr = artboard.getBoundingClientRect();
        const br = layer.getBoundingClientRect();
        const { x, y } = clientPoint(e);
        sx = x;
        sy = y;
        sl = ((br.left + br.width / 2 - cr.left) / cr.width) * 100;
        st = ((br.top + br.height / 2 - cr.top) / cr.height) * 100;

        if (e.type === 'touchstart') {
            touchPending = true;
            document.addEventListener('touchmove', touchDragMove, { passive: false });
            document.addEventListener('touchend', touchDragEnd);
            return;
        }

        e.preventDefault();
        dragging = true;
        layer.classList.add('is-dragging');
        document.addEventListener('mousemove', dragMove);
        document.addEventListener('mouseup', dragEnd);
    }

    function touchDragMove(e) {
        if (e.touches.length > 1) {
            touchPending = false;
            touchDragEnd();
            return;
        }
        if (!touchPending && !dragging) return;

        const { x, y } = clientPoint(e);
        const moved = Math.hypot(x - sx, y - sy);

        if (touchPending && !dragging && moved > 4) {
            touchPending = false;
            dragging = true;
            layer.classList.add('is-dragging');
        }

        if (!dragging) return;
        e.preventDefault();
        dragMove(e);
    }

    function touchDragEnd() {
        touchPending = false;
        dragEnd();
        document.removeEventListener('touchmove', touchDragMove);
        document.removeEventListener('touchend', touchDragEnd);
    }

    function dragMove(e) {
        if (!dragging) return;
        e.preventDefault();
        const cr = artboard.getBoundingClientRect();
        const { x, y } = clientPoint(e);
        const nx = Math.min(98, Math.max(2, sl + ((x - sx) / cr.width) * 100));
        const ny = Math.min(98, Math.max(2, st + ((y - sy) / cr.height) * 100));
        applyPosition(nx, ny);
    }

    function dragEnd() {
        dragging = false;
        layer.classList.remove('is-dragging');
        document.removeEventListener('mousemove', dragMove);
        document.removeEventListener('mouseup', dragEnd);
    }

    layer.addEventListener('mousedown', dragStart);
    layer.addEventListener('touchstart', dragStart, { passive: false });

    // ── Resize via corner handles (Canva-style) ─────────────
    layer.querySelectorAll('.locked-layer__handle').forEach((handle) => {
        let resizing = false;
        let startDist = 0;
        let startScale = 1;
        let centerX = 0;
        let centerY = 0;

        function resizeStart(e) {
            selectLayer();
            e.preventDefault();
            e.stopPropagation();
            resizing = true;
            layer.classList.add('is-resizing');
            startScale = currentScale();

            const br = layer.getBoundingClientRect();
            centerX = br.left + br.width / 2;
            centerY = br.top + br.height / 2;
            const pt = clientPoint(e);
            startDist = Math.hypot(pt.x - centerX, pt.y - centerY) || 1;

            document.addEventListener('mousemove', resizeMove);
            document.addEventListener('touchmove', resizeMove, { passive: false });
            document.addEventListener('mouseup', resizeEnd);
            document.addEventListener('touchend', resizeEnd);
        }

        function resizeMove(e) {
            if (!resizing) return;
            e.preventDefault();
            e.stopPropagation();
            const pt = clientPoint(e);
            const dist = Math.hypot(pt.x - centerX, pt.y - centerY) || 1;
            setScale(startScale * (dist / startDist));
        }

        function resizeEnd() {
            resizing = false;
            layer.classList.remove('is-resizing');
            document.removeEventListener('mousemove', resizeMove);
            document.removeEventListener('touchmove', resizeMove);
            document.removeEventListener('mouseup', resizeEnd);
            document.removeEventListener('touchend', resizeEnd);
        }

        handle.addEventListener('mousedown', resizeStart);
        handle.addEventListener('touchstart', resizeStart, { passive: false });
    });

    // ── Pinch zoom pada layer terpilih ────────────────────────
    let pinchStartDist = 0;
    let pinchStartScale = 1;

    layer.addEventListener('touchstart', (e) => {
        if (e.touches.length !== 2) return;
        selectLayer();
        pinchStartDist = touchDistance(e.touches);
        pinchStartScale = currentScale();
        layer.classList.add('is-resizing');
    }, { passive: true });

    layer.addEventListener('touchmove', (e) => {
        if (e.touches.length !== 2 || !pinchStartDist) return;
        e.preventDefault();
        const dist = touchDistance(e.touches);
        setScale(pinchStartScale * (dist / pinchStartDist));
    }, { passive: false });

    layer.addEventListener('touchend', () => {
        pinchStartDist = 0;
        layer.classList.remove('is-resizing');
    });

    // ── Wheel zoom (desktop) saat layer terpilih ──────────────
    layer.addEventListener('wheel', (e) => {
        selectLayer();
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.04 : 0.04;
        setScale(currentScale() + delta);
    }, { passive: false });

    layer.addEventListener('click', (e) => {
        e.stopPropagation();
        selectLayer();
    });

    // Init scale from data attribute / hidden input
    const initial = parseFloat(layer.dataset.layerScale || document.getElementById(`scale-${layerId}`)?.value || '1');
    if (Number.isFinite(initial) && initial !== 1) {
        setScale(initial, { silent: true });
    }
}

function flattenLayerPositions(artboard) {
    const ar = artboard.getBoundingClientRect();

    artboard.querySelectorAll('.locked-layer').forEach((layer) => {
        const lr = layer.getBoundingClientRect();
        layer.style.transform = 'none';
        layer.style.left = `${lr.left - ar.left}px`;
        layer.style.top = `${lr.top - ar.top}px`;
    });
}

function hideLayerChrome(artboard) {
    artboard.querySelectorAll('.locked-layer__hint, .locked-layer__handles').forEach((el) => {
        el.style.display = 'none';
    });
    artboard.querySelectorAll('.locked-layer').forEach((layer) => {
        layer.classList.remove('is-selected', 'is-dragging', 'is-resizing');
    });
}

/**
 * Siapkan artboard untuk export html2canvas — ukuran native + posisi px (tanpa transform).
 */
export async function prepareArtboardExport(artboard, globalScale = 1, layerScales = {}) {
    if (!artboard) return null;

    const w = parseInt(artboard.dataset.aspectW || '588', 10);
    const h = parseInt(artboard.dataset.aspectH || '423', 10);
    const shell = artboard.closest('.locked-canvas');

    normalizeCqwFonts(artboard, globalScale, layerScales);

    if (shell) {
        shell.style.width = `${w}px`;
        shell.style.height = `${h}px`;
        shell.style.maxWidth = `${w}px`;
        shell.style.aspectRatio = 'unset';
        shell.style.borderRadius = '0';
    }

    artboard.style.position = 'relative';
    artboard.style.width = `${w}px`;
    artboard.style.height = `${h}px`;
    artboard.style.inset = 'auto';

    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    normalizeCqwFonts(artboard, globalScale, layerScales);
    hideLayerChrome(artboard);
    flattenLayerPositions(artboard);

    await new Promise((resolve) => requestAnimationFrame(resolve));

    return { width: w, height: h, target: artboard, shell };
}

/**
 * @param {object} opts
 * @param {string} [opts.previewId]
 * @param {Record<string, number>} [opts.initialScales]
 */
export function initLockedCanvasEditor(opts = {}) {
    const previewId = opts.previewId || 'locked-preview';
    const preview = document.getElementById(previewId);
    const artboard = preview?.querySelector('[data-artboard]');
    if (!preview || !artboard) return null;

    const state = {
        selected: null,
        scales: { ...(opts.initialScales || {}) },
    };

    document.querySelectorAll('.locked-input').forEach((input) => {
        const bind = input.dataset.bind;
        const update = () => syncLayerText(artboard, bind, input.value);
        input.addEventListener('input', update);
        update();
    });

    artboard.querySelectorAll('.locked-layer--interactive').forEach((layer) => {
        makeInteractiveLayer(layer, artboard, state);
    });

    // Klik area kosong → deselect
    artboard.addEventListener('click', (e) => {
        if (e.target.closest('.locked-layer--interactive')) return;
        state.selected?.classList.remove('is-selected');
        state.selected = null;
    });

    normalizeCqwFonts(artboard, 1, state.scales);

    const ro = typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => normalizeCqwFonts(artboard, 1, state.scales))
        : null;
    ro?.observe(artboard);

    return {
        refreshFonts: () => normalizeCqwFonts(artboard, 1, state.scales),
        getScales: () => ({ ...state.scales }),
    };
}

if (typeof window !== 'undefined') {
    window.LockedCanvas = {
        normalizeCqwFonts,
        prepareArtboardExport,
        initLockedCanvasEditor,
    };
}
